import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Mail, Lock, Eye, EyeOff, User, Gift, ShieldCheck, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import { useLanguage } from '@/lib/LanguageContext';
import * as api from '@/lib/api';

// Google OAuth URL - Emergent managed
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_AUTH_URL = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + '/auth/callback')}`;

// Google Icon Component
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState('register');
  const [verificationCode, setVerificationCode] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [mockCode, setMockCode] = useState('');
  const [resending, setResending] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    referralCode: ''
  });

  useEffect(() => {
    const savedReferral = localStorage.getItem('referral_code');
    if (savedReferral) {
      setFormData(prev => ({ ...prev, referralCode: savedReferral }));
    }
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        referral_code: formData.referralCode
      });
      
      const { requires_verification, verification_code, user } = response.data;
      
      if (requires_verification) {
        setRegisteredEmail(formData.email);
        setStep('verify');
        if (verification_code) {
          setMockCode(verification_code);
          toast.info(`Código de verificação (modo teste): ${verification_code}`);
        } else {
          toast.success('Código de verificação enviado!');
        }
      } else {
        localStorage.removeItem('referral_code');
        toast.success('Conta criada com sucesso!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.verifyEmail(registeredEmail, verificationCode);
      localStorage.removeItem('referral_code');
      toast.success('Email verificado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      const response = await api.resendVerificationCode(registeredEmail);
      if (response.data.verification_code) {
        setMockCode(response.data.verification_code);
        toast.info(`Novo código (modo teste): ${response.data.verification_code}`);
      } else {
        toast.success('Código reenviado!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reenviar');
    } finally {
      setResending(false);
    }
  };

  // Verification Step
  if (step === 'verify') {
    return (
      <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="w-full max-w-sm">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-2xl font-heading font-bold mb-2">
                Verificar email
              </h1>
              <p className="text-muted-foreground text-sm">
                Enviamos um código para{' '}
                <span className="font-medium text-foreground">{registeredEmail}</span>
              </p>
            </div>

            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code" className="text-sm font-medium">
                  Código de verificação
                </Label>
                <Input
                  id="code"
                  type="text"
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-[0.5em] font-mono h-14 bg-muted/30 border-border/60"
                  maxLength={6}
                  required
                  data-testid="verification-code-input"
                />
              </div>

              {mockCode && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-center">
                  <p className="text-sm text-amber-400">
                    <strong>Modo Teste:</strong> {mockCode}
                  </p>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full h-12 text-base font-medium"
                disabled={loading || verificationCode.length !== 6}
                data-testid="verify-submit-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verificar'}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-3">
              <Button 
                variant="ghost" 
                onClick={handleResendCode}
                disabled={resending}
                className="text-sm"
                data-testid="resend-code-btn"
              >
                {resending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Reenviar código
              </Button>
              
              <Button 
                variant="link" 
                onClick={() => setStep('register')}
                className="text-sm text-muted-foreground block mx-auto"
              >
                Alterar email
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Registration Step
  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Logo and Title */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/logo.png" 
                alt="Viral Clipes" 
                className="w-16 h-16 mx-auto"
              />
            </Link>
            <h1 className="text-2xl font-heading font-bold mb-2">
              Criar conta
            </h1>
            <p className="text-muted-foreground text-sm">
              Comece a criar clips virais gratuitamente
            </p>
          </div>

          {/* Google Sign Up */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-12 gap-3 text-base mb-6 border-border/60 hover:bg-muted/50"
            onClick={() => window.location.href = GOOGLE_AUTH_URL}
            data-testid="google-register-btn"
          >
            <GoogleIcon />
            Continuar com Google
          </Button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border/60"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-3 text-muted-foreground">ou</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Nome completo
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Seu nome"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10 h-12 text-base bg-muted/30 border-border/60 focus:border-primary"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 h-12 text-base bg-muted/30 border-border/60 focus:border-primary"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Senha
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10 h-12 text-base bg-muted/30 border-border/60 focus:border-primary"
                  required
                  data-testid="password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="referral" className="text-sm font-medium">
                Código de indicação <span className="text-muted-foreground">(opcional)</span>
              </Label>
              <div className="relative">
                <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="referral"
                  type="text"
                  placeholder="ABCD1234"
                  value={formData.referralCode}
                  onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })}
                  className="pl-10 h-12 text-base bg-muted/30 border-border/60 focus:border-primary"
                  data-testid="referral-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-medium mt-2"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Criar conta'}
            </Button>
          </form>

          <p className="text-xs text-muted-foreground text-center mt-4">
            Ao criar uma conta, você concorda com nossos{' '}
            <Link to="/terms" className="text-primary hover:underline">Termos</Link> e{' '}
            <Link to="/privacy" className="text-primary hover:underline">Privacidade</Link>
          </p>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Já tem conta?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
              Entrar
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
